/**
 * Diamond Swimwear — PDP JavaScript
 * ────────────────────────────────────
 * Modules:
 *  1. Gallery: thumbnail navigation + swipe (mobile)
 *  2. Variant selector (swatch + size)
 *  3. Add to Cart with loading state
 *  4. Sticky mobile ATC bar
 *  5. Product tabs
 *  6. Lightbox / zoom
 *  7. Notify Me (back in stock)
 */

(function () {
  'use strict';

  const pdp = document.querySelector('[data-pdp-gallery]');
  if (!pdp) return; /* Only runs on PDP */

  const productData = JSON.parse(
    document.querySelector('[data-product-json]')?.textContent || 'null'
  );

  /* ═══════════════════════════════════════════════════
     1. GALLERY
     ═══════════════════════════════════════════════════ */
  const slides    = document.querySelectorAll('[data-pdp-slides] [data-slide]');
  const thumbs    = document.querySelectorAll('[data-thumb]');
  const dots      = document.querySelectorAll('[data-dot]');
  let currentSlide = 0;

  function goToSlide(idx) {
    if (!slides.length) return;
    idx = ((idx % slides.length) + slides.length) % slides.length;

    slides[currentSlide]?.classList.remove('is-active');
    thumbs[currentSlide]?.classList.remove('is-active');
    thumbs[currentSlide]?.setAttribute('aria-pressed', 'false');
    dots[currentSlide]?.classList.remove('is-active');

    currentSlide = idx;

    slides[currentSlide]?.classList.add('is-active');
    thumbs[currentSlide]?.classList.add('is-active');
    thumbs[currentSlide]?.setAttribute('aria-pressed', 'true');
    dots[currentSlide]?.classList.add('is-active');
  }

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => goToSlide(parseInt(thumb.dataset.thumb, 10)));
  });

  dots.forEach(dot => {
    dot.addEventListener('click', () => goToSlide(parseInt(dot.dataset.dot, 10)));
  });

  /* Arrow buttons */
  document.querySelector('[data-pdp-prev]')?.addEventListener('click', () => goToSlide(currentSlide - 1));
  document.querySelector('[data-pdp-next]')?.addEventListener('click', () => goToSlide(currentSlide + 1));

  /* Touch swipe (mobile) */
  const slideWrapper = document.querySelector('[data-pdp-slides]');
  if (slideWrapper) {
    let touchStartX = 0;
    slideWrapper.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    slideWrapper.addEventListener('touchend', e => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 40) goToSlide(currentSlide + (dx < 0 ? 1 : -1));
    });
  }

  /* ═══════════════════════════════════════════════════
     2. VARIANT SELECTOR
     ═══════════════════════════════════════════════════ */
  const form            = document.querySelector('[data-pdp-form]');
  const variantIdInput  = form && form.querySelector('[data-variant-id]');
  const priceEl         = document.querySelector('[data-pdp-price]');
  const colorSelectedEl = document.querySelector('[data-color-selected]');

  let selectedOptions = {};

  /* Init from current variant */
  if (productData) {
    const current = productData.variants.find(v =>
      v.id === parseInt(variantIdInput?.value, 10)
    );
    if (current) {
      productData.options.forEach((opt, i) => {
        selectedOptions[i] = current.options[i];
      });
    }
  }

  function findVariant() {
    if (!productData) return null;
    return productData.variants.find(v => {
      return productData.options.every((_, i) => v.options[i] === selectedOptions[i]);
    }) || null;
  }

  function updateVariant() {
    const variant = findVariant();
    if (!variant) return;

    /* Update hidden input */
    if (variantIdInput) variantIdInput.value = variant.id;

    /* Update URL */
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    /* Update price */
    if (priceEl) {
      if (variant.compare_at_price > variant.price) {
        priceEl.innerHTML = `
          <span class="dw-pdp__price-current dw-pdp__price-sale">${formatMoney(variant.price)}</span>
          <s class="dw-pdp__price-compare">${formatMoney(variant.compare_at_price)}</s>
          <span class="dw-badge dw-badge--sale">SALE</span>`;
      } else {
        priceEl.innerHTML = `<span class="dw-pdp__price-current">${formatMoney(variant.price)}</span>`;
      }
    }

    /* Update sticky ATC price */
    const stickyPrice = document.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

    /* Update gallery to variant image if available */
    if (variant.featured_image) {
      const imgIdx = Array.from(slides).findIndex(s => {
        const img = s.querySelector('img');
        return img && img.src.includes(variant.featured_image.id);
      });
      if (imgIdx > -1) goToSlide(imgIdx);
    }

    /* Update ATC availability */
    updateATCState(variant);

    /* Dispatch event for other components */
    document.dispatchEvent(new CustomEvent('dw:variant-changed', {
      detail: { variant, product: productData }
    }));
  }

  function updateATCState(variant) {
    const atcBtn      = document.querySelector('[data-atc-btn]');
    const notifyBtn   = document.querySelector('[data-notify-btn]');
    const stickyAtcBtn= document.querySelector('[data-sticky-atc-btn]');
    const atcText     = atcBtn && atcBtn.querySelector('[data-atc-text]');

    if (variant.available) {
      if (atcBtn) { atcBtn.disabled = false; atcBtn.style.display = ''; }
      if (notifyBtn) notifyBtn.style.display = 'none';
      if (atcText) atcText.textContent = 'Agregar al carrito';
      if (stickyAtcBtn) { stickyAtcBtn.disabled = false; stickyAtcBtn.textContent = 'Agregar al carrito'; }
    } else {
      if (atcBtn) atcBtn.style.display = 'none';
      if (notifyBtn) notifyBtn.style.display = '';
      if (stickyAtcBtn) { stickyAtcBtn.disabled = true; stickyAtcBtn.textContent = 'Agotado'; }
    }
  }

  /* Swatch buttons */
  document.querySelectorAll('[data-swatch-btn]').forEach(btn => {
    btn.addEventListener('click', () => {
      const optionIndex = parseInt(btn.dataset.swatchBtn, 10);
      const value       = btn.dataset.swatchValue;

      document.querySelectorAll(`[data-swatch-btn="${optionIndex}"]`)
        .forEach(b => { b.classList.remove('is-selected'); b.setAttribute('aria-pressed', 'false'); });

      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');

      selectedOptions[optionIndex] = value;

      if (colorSelectedEl) colorSelectedEl.textContent = value;

      updateVariant();
    });
  });

  /* Size buttons */
  document.querySelectorAll('[data-size-btn]').forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.classList.contains('is-unavailable')) return;

      const optionIndex = parseInt(btn.dataset.sizeBtn, 10);
      const value       = btn.dataset.sizeValue;

      document.querySelectorAll(`[data-size-btn="${optionIndex}"]`)
        .forEach(b => { b.classList.remove('is-selected'); b.setAttribute('aria-pressed', 'false'); });

      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');

      selectedOptions[optionIndex] = value;

      const sizeSelectedEl = document.querySelector('[data-size-selected]');
      if (sizeSelectedEl) sizeSelectedEl.textContent = value;

      updateVariant();
    });
  });

  /* Large swatch buttons — also update color label */
  document.querySelectorAll('[data-swatch-btn]').forEach(btn => {
    btn.addEventListener('click', () => {
      const colorSelectedEl2 = document.querySelector('[data-color-selected]');
      if (colorSelectedEl2) colorSelectedEl2.textContent = btn.dataset.swatchValue;
    });
  });

  /* ═══════════════════════════════════════════════════
     2b. QUANTITY SELECTOR
     ═══════════════════════════════════════════════════ */
  const qtyInput = document.querySelector('[data-qty-input]');
  document.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
    if (!qtyInput) return;
    const v = parseInt(qtyInput.value, 10) || 1;
    if (v > 1) qtyInput.value = v - 1;
  });
  document.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
    if (!qtyInput) return;
    const v = parseInt(qtyInput.value, 10) || 1;
    qtyInput.value = v + 1;
  });

  /* ═══════════════════════════════════════════════════
     3. ADD TO CART
     ═══════════════════════════════════════════════════ */
  async function doAddToCart() {
    const atcBtn  = form && form.querySelector('[data-atc-btn]');
    const atcText = atcBtn && atcBtn.querySelector('[data-atc-text]');
    const spinner = atcBtn && atcBtn.querySelector('.dw-pdp__atc-spinner');
    const varId   = parseInt(variantIdInput?.value, 10);
    const qty     = parseInt(qtyInput?.value, 10) || 1;
    if (!varId) return;

    /* Enter loading state */
    if (atcBtn) { atcBtn.setAttribute('data-loading', 'true'); atcBtn.disabled = true; }
    if (spinner) spinner.style.display = 'flex';
    if (atcText) atcText.textContent = '...';

    try {
      /* ── Call Shopify Cart API directly ── */
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: varId, quantity: qty })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.description || `HTTP ${res.status}`);
      }

      /* ── Fetch updated cart for badge + drawer ── */
      const cartRes  = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cartData = await cartRes.json();

      /* Update cart badge count */
      document.dispatchEvent(new CustomEvent('dw:cart-updated', { detail: cartData }));

      /* Open cart drawer */
      document.dispatchEvent(new CustomEvent('dw:cart-open'));

      if (atcText) atcText.textContent = '¡Añadido!';
      setTimeout(() => {
        if (atcText) atcText.textContent = 'Agregar al carrito';
      }, 2000);

    } catch (err) {
      console.error('[DW ATC]', err);
      /* Show error in button briefly */
      if (atcText) atcText.textContent = 'Error — intenta de nuevo';
      setTimeout(() => {
        if (atcText) atcText.textContent = 'Agregar al carrito';
      }, 3000);
    } finally {
      if (atcBtn) { atcBtn.setAttribute('data-loading', 'false'); atcBtn.disabled = false; }
      if (spinner) spinner.style.display = 'none';
    }
  }

  if (form) {
    form.addEventListener('submit', e => {
      e.preventDefault();
      doAddToCart();
    });
  }

  /* Sticky ATC button */
  document.querySelector('[data-sticky-atc-btn]')?.addEventListener('click', () => {
    doAddToCart();
  });

  /* ═══════════════════════════════════════════════════
     4. STICKY MOBILE ATC BAR
     ═══════════════════════════════════════════════════ */
  const stickyAtc = document.querySelector('[data-sticky-atc]');
  const atcRow    = document.querySelector('[data-atc-btn]');

  if (stickyAtc && atcRow) {
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        stickyAtc.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { threshold: 0.5 });

    io.observe(atcRow);
  }

  /* ═══════════════════════════════════════════════════
     5. PRODUCT TABS
     ═══════════════════════════════════════════════════ */
  document.querySelectorAll('[data-pdp-tabs] [data-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      const tabId  = btn.dataset.tab;
      const panels = document.querySelectorAll('.dw-pdp__tab-panel');
      const tabBtns= document.querySelectorAll('[data-tab]');

      tabBtns.forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
      panels.forEach(p => { p.classList.remove('is-active'); p.hidden = true; });

      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');

      const panel = document.getElementById(`tab-panel-${tabId}`);
      if (panel) { panel.classList.add('is-active'); panel.hidden = false; }
    });
  });

  /* ═══════════════════════════════════════════════════
     6. LIGHTBOX ZOOM
     ═══════════════════════════════════════════════════ */
  document.querySelectorAll('[data-zoom]').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const src  = trigger.dataset.zoom;
      const lb   = createLightbox(src);
      document.body.appendChild(lb);
      document.body.classList.add('modal-open');
      lb.querySelector('[data-lb-close]').focus();
    });
  });

  function createLightbox(src) {
    const lb = document.createElement('div');
    lb.className = 'dw-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-label', 'Zoom de imagen');
    lb.innerHTML = `
      <div class="dw-lightbox__backdrop" data-lb-close></div>
      <div class="dw-lightbox__inner">
        <button class="dw-lightbox__close" data-lb-close aria-label="Cerrar zoom">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M3 3l16 16M19 3L3 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <img src="${src}" class="dw-lightbox__img" alt="Zoom del producto" loading="eager">
      </div>`;

    lb.querySelectorAll('[data-lb-close]').forEach(el =>
      el.addEventListener('click', () => {
        lb.remove();
        document.body.classList.remove('modal-open');
      })
    );

    document.addEventListener('keydown', function onKey(e) {
      if (e.key === 'Escape') { lb.remove(); document.body.classList.remove('modal-open'); document.removeEventListener('keydown', onKey); }
    });

    return lb;
  }

  /* ═══════════════════════════════════════════════════
     7. NOTIFY ME (Back in Stock)
     ═══════════════════════════════════════════════════ */
  document.querySelector('[data-notify-btn]')?.addEventListener('click', () => {
    const email = prompt('Ingresa tu correo para ser notificada cuando este producto vuelva a estar disponible:');
    if (email && email.includes('@')) {
      alert('¡Gracias! Te enviaremos un correo cuando este artículo vuelva a estar disponible.');
    }
  });

  /* ── Utility ─────────────────────────────────────── */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

})();
